import { useEffect, useRef, type ReactNode } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Chip, CircularProgress, Link, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  FAMILIARITY_LABELS,
  FIRST_CHOICE_LABELS,
  REFERRAL_STATUS_LABELS,
  type HouseholdDetail,
  type ReferralChild,
  type ReferralListItem,
  type ReferralReviewDetail,
} from '@hv/shared';
import { useReferralDetail, useReferralList } from '../api/hooks';
import { useAuth } from '../components/AuthContext';
import { describeError } from '../components/Notify';
import { ReferralStatusChip, ReferralStatusControl } from '../referrals/ReferralStatusControl';
import { BRAND } from '../theme';
import { useUrlState } from '../urlState';
import { Section } from './Section';

const formatDateTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

export function ReferralSection({ h }: { h: HouseholdDetail }) {
  const referrals = useReferralList({ householdId: h.id, pageSize: 100 });
  if (referrals.error)
    return (
      <Section title="Trustee referral">
        <Alert severity="error">Could not load referrals: {describeError(referrals.error)}</Alert>
      </Section>
    );
  const items = referrals.data?.items ?? [];
  if (!items.length) return null;
  return (
    <Section title={items.length === 1 ? 'Trustee referral' : 'Trustee referrals'}>
      <Stack gap={2}>
        {items.map((r) => (
          <ReferralCard key={r.id} item={r} />
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary" component="p" mt={1.5}>
        Internal notes go in{' '}
        <Link component="button" variant="caption" onClick={() => document.getElementById('section-activity-notes')?.scrollIntoView({ behavior: 'smooth' })}>
          Activity &amp; Notes
        </Link>
        . Trustees never see notes or status reasons.
      </Typography>
    </Section>
  );
}

function ReferralCard({ item }: { item: ReferralListItem }) {
  const { can } = useAuth();
  const { params } = useUrlState();
  const detail = useReferralDetail(item.id);
  const ref = useRef<HTMLDivElement>(null);
  const focused = params.get('r') === item.id;
  const d = detail.data;

  useEffect(() => {
    if (focused && d) ref.current?.scrollIntoView({ block: 'start' });
  }, [focused, d]);

  return (
    <Box ref={ref} data-testid="referral-card" aria-label={`Referral ${item.referenceNumber}`} role="group" sx={{ p: 1.5, border: `1px solid ${focused ? BRAND.navy : BRAND.line}`, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Typography fontWeight={700} sx={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>
            {item.referenceNumber}
          </Typography>
          <ReferralStatusChip status={(d ?? item).status} />
          {item.countsTowardAllotment && <Chip size="small" variant="outlined" color="secondary" label="Recommendation" />}
        </Stack>
        {d && <ReferralStatusControl referral={d} canChange={can('referrals.review')} />}
      </Stack>
      <Typography variant="body2" color="text.secondary" mt={0.5}>
        {item.trustee.displayName || item.trustee.email} ({item.trustee.email}) · {item.cycle?.label ?? 'No cycle'} · submitted {formatDateTime(item.submittedAt)}
      </Typography>
      {detail.isPending && <CircularProgress size={20} aria-label="Loading referral" sx={{ mt: 1 }} />}
      {detail.error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Could not load the referral: {describeError(detail.error)}
        </Alert>
      )}
      {d && <ReferralDetailBody d={d} />}
    </Box>
  );
}

function ReferralDetailBody({ d }: { d: ReferralReviewDetail }) {
  return (
    <>
      {d.status === 'needs_clarification' && d.clarificationRequest && (
        <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="clarification-request">
          <strong>Clarification requested {formatDateTime(d.clarificationRequestedAt)}:</strong> {d.clarificationRequest}
        </Alert>
      )}
      <Accordion disableGutters variant="outlined" sx={{ mt: 1.5 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Responses</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Responses d={d} />
        </AccordionDetails>
      </Accordion>
      <Typography fontWeight={600} mt={1.5} mb={0.5}>
        Status history
      </Typography>
      <Box component="ol" sx={{ m: 0, pl: 2.5 }} aria-label="Status history">
        {d.history.map((e) => (
          <Typography component="li" variant="body2" key={e.id} sx={{ mb: 0.5 }}>
            {formatDateTime(e.at)} · {e.fromStatus ? `${REFERRAL_STATUS_LABELS[e.fromStatus]} → ` : ''}
            {REFERRAL_STATUS_LABELS[e.toStatus]}
            {e.changedByName ? ` · ${e.changedByName}` : ''}
            {e.reason && (
              <Box component="span" sx={{ display: 'block', color: BRAND.muted, whiteSpace: 'pre-wrap' }}>
                {e.reason}
              </Box>
            )}
          </Typography>
        ))}
      </Box>
    </>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  if (children === '' || children === null || children === undefined) return null;
  return (
    <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
      <Box component="span" sx={{ color: BRAND.muted, fontWeight: 600 }}>
        {label}:{' '}
      </Box>
      {children}
    </Typography>
  );
}

function childName(c: ReferralChild) {
  const full = [c.legalFirstName, c.middleName, c.lastName].filter((x) => x.trim()).join(' ');
  return c.preferredName.trim() ? `${full} (goes by ${c.preferredName.trim()})` : full;
}

function Responses({ d }: { d: ReferralReviewDetail }) {
  const p = d.payload;
  return (
    <Box>
      <Field label="Family last name">{p.familyLastName}</Field>
      {p.guardians.map((g, i) => (
        <Field key={i} label={`Parent/guardian ${i + 1}`}>
          {[`${g.firstName} ${g.lastName}`.trim(), g.email, g.phone].filter(Boolean).join(' · ')}
        </Field>
      ))}
      <Field label="Relationship to the family">{p.relationship}</Field>
      <Field label="Connection">{p.connectionNarrative}</Field>
      <Field label="Contribution to the community">{p.communityContribution}</Field>
      <Field label="Gulliver is their first choice">{FIRST_CHOICE_LABELS[p.firstChoice]}</Field>
      <Field label="Enrolment interest">{p.enrollmentInterest}</Field>
      <Field label="Additional information">{p.additionalInformation}</Field>
      {p.children.map((c) => (
        <Box key={c.childKey} sx={{ mt: 1.5, pt: 1, borderTop: `1px solid ${BRAND.line}` }}>
          <Typography variant="body2" fontWeight={700}>
            {childName(c)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {[c.grade && `Grade ${c.grade}`, c.currentSchool, [c.city, c.stateOrCountry].filter((x) => x.trim()).join(', ')].filter(Boolean).join(' · ')}
          </Typography>
          <Field label="Academic standing">
            {FAMILIARITY_LABELS[c.academicFamiliarity]}
            {c.academicContext.trim() ? ` — ${c.academicContext}` : ''}
          </Field>
          <Field label="Disciplinary standing">
            {FAMILIARITY_LABELS[c.disciplinaryFamiliarity]}
            {c.disciplinaryContext.trim() ? ` — ${c.disciplinaryContext}` : ''}
          </Field>
        </Box>
      ))}
    </Box>
  );
}
